import React, {FC, useEffect} from "react";
import {useRecoilState, useRecoilValue} from "recoil";
import {AccessContextData, AppContext, FetchingPotentials, LoggedIn} from "../../providers/access/AppContextProvider";
import {useLoginProviderLoginMutation, useLoginProviderRequestPotentialsMutation} from "../../Queries";
import {LocaleAtom} from "../../providers/localization/LocalizationProvider";
import {setPreview} from "./PreviewContext";
import {useNavigate} from "react-router-dom";
import {PotentialsRequestDialog} from "./PotentialsRequestDialog";
import {LoginContext} from "../LoginContext";
import {Progress} from "../../components/Progress";

/**
 * Helper function to determine the correct country code,
 * with special handling for known problematic stores.
 */
const getCountryCode = (): string => {
   // Special case for the known problematic store
   if (window.Shopify?.shop === "givn-clothing.myshopify.com" && window.Shopify?.locale === "de") {
      return "DE";
   }
   
   // Default fallback to regular Shopify country or session country code
   return window.Shopify?.country || window.tradeInSession?.countryCode || "";
};

export type LoginProps = {
   accessToken: string
   preview: boolean
}

export const LoginProvider: FC<LoginProps> = ({accessToken, preview, children}) => {
   const [appContext, setAppContext] = useRecoilState(AccessContextData);
   const [loginContext, setLoginContext] = useRecoilState(LoginContext);
   const [locale, setLocale] = useRecoilState(LocaleAtom);
   const loggedIn = useRecoilValue(LoggedIn);
   const fetchingPotentials = useRecoilValue(FetchingPotentials);
   const [login] = useLoginProviderLoginMutation();
   const [requestPotentials] = useLoginProviderRequestPotentialsMutation();
   const navigate = useNavigate();

   useEffect(() => {
      setPreview(preview);
      if (!loggedIn) {
         (async function () {
            const loginResult = await login({
               variables: {
                  shopId: appContext.shopId,
                  accessToken: accessToken,
                  preview: preview,
                  multiLogin: appContext.multiLogin,
                  currency: window.Shopify?.currency?.active || window.tradeInSession?.currency,
                  language: window.Shopify?.locale || window.tradeInSession?.language,
                  countryCode: getCountryCode()
               }
            });
            if (!loginResult?.data?.ConsumerLogin?.token) {
               navigate('/')
               return;
            }
            setLoginContext({
               ...loginContext,
               token: loginResult.data.ConsumerLogin.token
            })

            const appContextUpdated: AppContext = {
               ...appContext,
               shopId: appContext.multiLogin ? loginResult.data?.ConsumerLogin?.shopId || appContext.shopId : appContext.shopId,
               consumerId: loginResult.data?.ConsumerLogin?.consumerId,
               locale: loginResult.data?.ConsumerLogin?.locale,
               allowedCountry: loginResult.data?.ConsumerLogin?.allowedCountry,
               wholeSaleActive: loginResult.data?.ConsumerLogin?.wholeSaleActive,
               brandName: loginResult.data?.ConsumerLogin?.brandName,
               currency: loginResult.data?.ConsumerLogin?.currency,
               shopCurrency: loginResult.data?.ConsumerLogin?.shopCurrency,
               circularPoints: loginResult.data?.ConsumerLogin?.circularPoints,
               lockEmail: loginResult.data?.ConsumerLogin?.lockEmail,
               requestPotentialsSupported: loginResult.data?.ConsumerLogin?.requestPotentialsSupported,
               tradeInComments: loginResult.data?.ConsumerLogin?.tradeInComments || false,
               tradeInPayoutEnabled: loginResult.data?.ConsumerLogin?.tradeInPayoutEnabled || false,
               tradeInPayoutPct: loginResult.data?.ConsumerLogin?.tradeInPayoutPct || 0,
            };

            if (appContextUpdated.requestPotentialsSupported) {
               const {data} = await requestPotentials({
                  variables: {
                     shopId: appContextUpdated.shopId,
                     consumerId: appContextUpdated.consumerId
                  }
               });
               appContextUpdated.requestPotentialTokens = data?.ConsumerRequestPotentials || []
            }

            setAppContext(appContextUpdated);

            if (locale !== loginResult.data?.ConsumerLogin?.locale) {
               setLocale(loginResult.data?.ConsumerLogin?.locale!);
            }
         })();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   const onCompletePotentials = async () => {
      setAppContext({
         ...appContext,
         requestPotentialTokens: []
      });
   }

   const nonInitialized = !appContext.consumerId
   if (nonInitialized) {
      return <Progress/>
   }

   return <>
      {fetchingPotentials &&
         <PotentialsRequestDialog onComplete={onCompletePotentials}/>
      }
      {(!fetchingPotentials && !loggedIn) && <Progress/>}
      {(!fetchingPotentials && loggedIn) && children}
   </>
}
